import { ReactElement, useEffect, useState, useContext } from 'react'
import MapGl, {
  Marker,
  useMap,
  GeolocateControl,
  AttributionControl,
} from 'react-map-gl'
import styled, { useTheme, ThemeContext } from 'styled-components'
import { device } from '../breakpoints'
import IcomoonReact from 'icomoon-react'
import coneSet from './selection.json'

const isMobile = window.innerWidth < 768
// Fly to the active marker
const FlyManager = ({ lat, lng }: { lat: number; lng: number }) => {
  const { current: map } = useMap()

  if (lat && lng && map) {
    map.flyTo({ center: [lng, lat] })
  }

  return <></>
}

const Map = ({
  handleMarkerClick,
  markers,
  handleMapLoaded,
  handleUserLocationFound,
  handleUserLocationError,
  activeMarker,
  locked,
  publisherDomain,
  locateMe,
  customStyle,
}: {
  handleMarkerClick: (marker: any) => void
  markers: any[]
  handleMapLoaded: () => void
  handleUserLocationFound: (lat: number, lng: number) => void
  handleUserLocationError: (e: GeolocationPositionError) => void
  activeMarker: any
  locked: boolean
  publisherDomain: string | undefined
  locateMe: boolean
  customStyle?: string | null
}): ReactElement => {
  const [geolocateControlRef, setGeolocateControlRef] = useState<any>(null)
  const themeContext = useContext(ThemeContext)
  const theme = useTheme()

  // Get the user location
  useEffect(() => {
    if (locateMe && geolocateControlRef) {
      console.log('Trigger geolocateControl')
      geolocateControlRef.trigger()
    }
  }, [locateMe, geolocateControlRef])

  // We need to center and zoom the map according to the tenant
  let center = [6.627086027591877, 46.52656029698641]
  if (publisherDomain === 'tdg') center = [6.148, 46.22]
  else if (publisherDomain === 'tagesanzeiger') center = [8.542, 47.375]

  let zoom = isMobile ? 10.5 : 12
  // if (publisherDomain === 'tdg') zoom = isMobile ? 12 : 13
  // else if (publisherDomain === 'tagesanzeiger') zoom = isMobile ? 12 : 13
  const boundMargin = 0.2
  const bounds = [
    [5.952428634541519 - boundMargin, 46.11477554816955 - boundMargin], // sud ouest
    [6.253522356354125 + boundMargin, 46.28088402323576 + boundMargin], // nord est
  ]

  const mapStyle =
    themeContext?.colorMode === 'light'
      ? 'clv12tj80008q01pkbpk6bz63'
      : 'clw9aqljd005g01r024fp65g6'

  return (
    <Container>
      <MapGl
        scrollZoom={isMobile ? true : !locked}
        dragPan={isMobile ? true : !locked}
        pitchWithRotate={false}
        dragRotate={false}
        minZoom={isMobile ? 10.5 : 11}
        maxZoom={16}
        maxBounds={bounds}
        onLoad={() => {
          handleMapLoaded()
        }}
        // onZoom={(e) => {
        //   console.log(e.viewState.zoom)
        // }}
        initialViewState={{
          longitude: center[0],
          latitude: center[1],
          zoom: zoom,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={
          customStyle
            ? customStyle
            : `mapbox://styles/ta-interaktiv/${mapStyle}`
        }
        // specific token for traffic density
        mapboxAccessToken='pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjbHc2a2g4d3IxbWwwMmttcWJ0ZTQxZ3U4In0.gY6j1_ZLq28oWtsLARd01A'
      >
        {/* <AttributionControl compact={true} customAttribution='x stuff xyasdfadsfadfs dfsafdsx' /> */}
        {/* <GeolocateControl
          ref={setGeolocateControlRef}
          position='bottom-right'
          onGeolocate={(res) => {
            handleUserLocationFound(res.coords.latitude, res.coords.longitude)
          }}
          onError={(e: GeolocationPositionError) => {
            handleUserLocationError(e)
          }}
        /> */}
        {activeMarker && (
          <FlyManager
            lat={activeMarker.latitude}
            lng={activeMarker.longitude}
          />
        )}
        {!customStyle &&
          markers.map((marker) => (
            <Marker
              key={marker.markerId}
              longitude={marker.longitude}
              latitude={marker.latitude}
              anchor='center'
              style={{
                zIndex: marker.markerId === activeMarker?.markerId ? 1 : 0,
              }}
            >
              <MarkerContent
                $active={marker.markerId === activeMarker?.markerId}
                onClick={() => handleMarkerClick(marker)}
              >
                <IcomoonReact
                  iconSet={coneSet}
                  icon='cone3'
                  size={20}
                  color={
                    marker.markerId === activeMarker?.markerId
                      ? '#ffffff'
                      : marker?.new
                      ? '#9E272F'
                      : '#E16D00'
                  }
                ></IcomoonReact>
              </MarkerContent>
            </Marker>
          ))}
      </MapGl>
    </Container>
  )
}

export default Map

const Container = styled.div`
  position: relative;
  background-color: #f5f6f7;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
    margin-left: 0%;
    height: 100%;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 100%;
    margin-left: 0%;
    height: 100vh;
  }
`

const MarkerContent = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $active, theme }) =>
    $active
      ? theme.mainColor
      : `rgba(
      ${theme.colorMode === 'light' ? '255, 255, 255' : '18,18,18'},
      0.5
    );`};
  width: 28px;
  height: 28px;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
`
